/**
 * The Object Explorer's connection row label — name, engine tag, and the
 * "this connection isn't open" treatment.
 *
 * FOUR different not-open states share that row, and they must not look alike:
 *
 * - **disconnected** — the connection failed, dropped, or the user hung up on
 *   it. Amber warning triangle + "Disconnected": something went wrong, or at
 *   minimum the user has to act to get it back.
 * - **connecting** — a dial is in flight for this connection RIGHT NOW.
 *   Spinner + "Connecting…", and no reconnect button: there is nothing to
 *   retry, it is already happening.
 *
 *   This state has to be read from `connectionOperationById`, not from the
 *   pending set below, because `requestLazyConnect` consumes the pending id at
 *   the START of the dial. Without it the whole dial window — every second a
 *   slow or unreachable server takes — rendered as the amber FAILURE state,
 *   which is the single loudest way to tell a user something is broken while
 *   it is in fact working. "It showed Disconnected for some time and then it
 *   worked" is that gap, reported.
 * - **pending (background)** — a resume seeded it closed and the post-restore
 *   sweep (`sessions/services/lazyConnect.ts`) is about to open it. Nothing is
 *   wrong and nothing is asked of the user. Muted clock + "Connecting shortly".
 * - **pending (password)** — the sweep skipped it because opening it needs the
 *   password prompt, which is single-instance. This one really does wait for
 *   first use, and the row says what it is waiting for: muted key + "Needs
 *   password". Telling this user "Connects on first use" while every other
 *   connection came back by itself was the copy that stopped being true.
 *
 * Pending is read LIVE from lazy connect's pending set instead of being baked
 * into `metadata.status`. Two reasons: that set is the authority (it is
 * consumed the instant a connect is dialed and forgotten when the user
 * disconnects), and the tree's connection metadata is only rebuilt when the
 * connection IDS change — never on a status flip — so a snapshot would go
 * stale exactly when it matters. Keeping `status` at `'disconnected'` also
 * leaves every existing gate that reads it (skip database preload, refresh →
 * reconnect routing, the workspace banner) behaving identically: deferred is
 * an explanation layered on top of disconnected, not a replacement for it.
 */
import { useState } from 'react';
import { AlertTriangle, Clock, KeyRound, RefreshCw } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import { getEngineShortLabel } from '../../../connections/components/ConnectionEngineIcon';
import { useLazyConnectPendingKind } from '../../../sessions/hooks/useLazyConnectPendingKind';
import { useConnectionStore } from '../../../connections/store/connectionStore';
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import { useIsConnectionSchemaRefreshing } from '../../../intellisense/schemaRefresh/schemaRefreshStore';
import type { ConnectionTreeNode } from '../../models/nodeTypes';
import { ReadOnlyConnectionBadge } from '../../../connections/components/ReadOnlyConnectionBadge';

interface ConnectionNodeLabelProps {
  node: ConnectionTreeNode;
  /** Absent when the tree is rendered without a reconnect affordance. */
  onReconnect?: (connectionId: string) => Promise<boolean>;
}

export function ConnectionNodeLabel({ node, onReconnect }: ConnectionNodeLabelProps) {
  const [isReconnecting, setIsReconnecting] = useState(false);
  const isDisconnected = node.metadata?.status === 'disconnected';
  // Gate on `isDisconnected` too: a connection that is already open is never
  // "waiting", whatever bookkeeping says.
  const pendingKind = useLazyConnectPendingKind(node.metadata?.id);
  const isDeferred = pendingKind !== 'none' && isDisconnected;
  const needsPassword = isDeferred && pendingKind === 'password';
  // Live dial state — the store sets this for the whole of `connectToDatabase`
  // and clears it in a `finally`, so it covers user connects, the resume's
  // eager connects and the background sweep alike.
  const isConnecting = useConnectionStore(
    (state) => state.connectionOperationById[node.metadata?.id ?? ''] === 'connecting'
  );
  // A manual IntelliSense refresh for any database on this connection —
  // visible here so a collapsed connection still shows the work in flight.
  const isSchemaRefreshing = useIsConnectionSchemaRefreshing(node.metadata?.id);
  // Round B U32 (every engine): a read-only connection says so on its row, not only through its alias.
  const isReadOnly = useConnectionStore(
    (state) => state.connections.find((connection) => connection.id === node.metadata?.id)?.readOnly === true
  );

  const accent = isDeferred
    ? 'text-muted-foreground'
    : 'text-warning';

  return (
    <span
      className={cn(
        'flex min-w-0 items-center gap-1.5',
        isDisconnected && 'text-muted-foreground'
      )}
    >
      {/* Round B U30: the name truncates (full name as tooltip) so a long alias never pushes the engine tag onto a
          second line and every connection row keeps one height. */}
      <span className="min-w-0 truncate" title={node.name}>{node.name}</span>
      <span className="text-[10px] text-muted-foreground/70 shrink-0">
        {getEngineShortLabel(node.metadata?.engine)}
      </span>
      {isReadOnly && <ReadOnlyConnectionBadge testId="explorer-read-only-badge" />}
      {!isDisconnected && isSchemaRefreshing && (
        <span
          className="inline-flex shrink-0"
          data-testid="explorer-connection-intellisense-refreshing"
          title="Refreshing IntelliSense…"
        >
          <RandomDotmSpinner size={11} className="text-muted-foreground/80" />
        </span>
      )}
      {isDisconnected && (
        <>
          {isConnecting ? (
            <span
              className="flex items-center gap-1 shrink-0"
              data-testid="explorer-connecting-badge"
              title="Opening this connection…"
            >
              <RandomDotmSpinner size={11} className="text-muted-foreground/80" />
              <span className="text-[10px] text-muted-foreground font-medium">
                Connecting…
              </span>
            </span>
          ) : isDeferred ? (
            <span
              className="flex items-center gap-1 shrink-0"
              data-testid="explorer-deferred-badge"
              data-deferred-reason={needsPassword ? 'password' : 'background'}
              title={
                needsPassword
                  ? 'This connection asks for its password, so the resume left it closed — it opens the first time a tab needs it.'
                  : 'The resume is opening this connection in the background.'
              }
            >
              {needsPassword ? (
                <KeyRound className="h-3.5 w-3.5 text-muted-foreground/80" aria-hidden />
              ) : (
                <Clock className="h-3.5 w-3.5 text-muted-foreground/80" aria-hidden />
              )}
              <span className="text-[10px] text-muted-foreground font-medium">
                {needsPassword ? 'Needs password' : 'Connecting shortly'}
              </span>
            </span>
          ) : (
            <>
              <AlertTriangle className="h-3.5 w-3.5 text-warning shrink-0" />
              <span className="text-[10px] text-warning font-medium shrink-0">
                Disconnected
              </span>
            </>
          )}
          {onReconnect && !isConnecting && (
            <button
              className={cn(
                'ml-1 p-0.5 rounded transition-[background-color,scale] active:scale-[0.96] shrink-0',
                isDeferred
                  ? 'hover:bg-muted'
                  : 'hover:bg-amber-200 dark:hover:bg-amber-800'
              )}
              title={isDeferred ? 'Connect now' : 'Try to reconnect'}
              data-testid="explorer-reconnect-button"
              onClick={async (e) => {
                e.stopPropagation();
                if (isReconnecting) return;
                setIsReconnecting(true);
                try {
                  await onReconnect(node.metadata.id);
                } finally {
                  setIsReconnecting(false);
                }
              }}
            >
              {isReconnecting ? (
                <RandomDotmSpinner size={11} className={cn(accent, 'opacity-90')} />
              ) : (
                <RefreshCw className={cn('h-3 w-3', accent)} />
              )}
            </button>
          )}
        </>
      )}
    </span>
  );
}
